/**
 * @fileoverview Left-sidebar navigation for the redesigned student portal
 * ("Studio Quiet"). Brand block + a "Student Portal" role badge + the single
 * yellow CTA + Book a lesson + primary nav + Sign out pinned to the bottom.
 * Shared by the fixed desktop rail and the mobile drawer (see
 * StudentPortalLayout). Port of the design handoff's StudentSidebar.
 *
 * Since C6/C7 (Nov 2026) the yellow CTA is Log practice where practice logs
 * are on (Record rehearsal otherwise), each teacher's live booking page sits
 * under it, and Portfolio is Progress, with sharing behind it.
 */

import { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { StudentAuthContext } from "@/App";
import { displayName } from "@/lib/userName";
import { Icon } from "@/components/studio/primitives";
import { companyName } from "@/lib/companyBrand";
import { practiceLogsEnabled, rehearsalsEnabled } from "@/lib/featureFlags";
import { bookHint, bookLabel } from "@/lib/studentBooking";
import { useStudentPractice } from "@/components/StudentPracticeContext";

const PRIMARY_NAV = [
  { id: "dashboard", label: "Dashboard", icon: "grid", path: "/student/dashboard" },
  { id: "lessons", label: "Lessons", icon: "fileText", path: "/student/lessons" },
  { id: "rehearsals", label: "Rehearsals", icon: "mic", path: "/student/rehearsals" },
  { id: "progress", label: "Progress", icon: "trendUp", path: "/student/progress" },
  { id: "profile", label: "Profile", icon: "user", path: "/student/profile" },
];

/** Which nav item owns the current path. */
export function activeStudentNavId(pathname) {
  if (pathname.startsWith("/student/lessons")) return "lessons";
  if (pathname.startsWith("/student/rehearsals")) return "rehearsals";
  if (pathname.startsWith("/student/progress") || pathname.startsWith("/student/portfolio")) return "progress";
  if (pathname.startsWith("/student/profile")) return "profile";
  return "dashboard";
}

function NavItem({ item, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item.path)}
      aria-current={active ? "page" : undefined}
      data-testid={`student-nav-${item.id}`}
      className={[
        "flex items-center gap-[14px] rounded-[12px] border px-[14px] py-[11px] text-left transition-colors duration-150",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]"
          : "border-transparent hover:bg-[var(--studio-raised)]",
      ].join(" ")}
    >
      <Icon
        name={item.icon}
        size={20}
        strokeWidth={active ? 2 : 1.7}
        className={active ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]"}
      />
      <span
        className={[
          "flex-1 font-sans text-base",
          active ? "font-semibold text-[var(--text-primary)]" : "font-medium text-[var(--text-secondary)]",
        ].join(" ")}
      >
        {item.label}
      </span>
      {active && <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-yellow)]" />}
    </button>
  );
}

/**
 * The sidebar's inner content. `onNavigate` fires after any navigation so the
 * mobile drawer can close itself.
 *
 * @param {Object} props
 * @param {() => void} [props.onNavigate]
 * @param {Array<Object>} [props.bookingLinks] - The teachers' live booking pages (read by the layout).
 */
export function StudentSidebarInner({ onNavigate, bookingLinks = [] }) {
  const { student, logout } = useContext(StudentAuthContext) || {};
  const { openLog } = useStudentPractice();
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeStudentNavId(location.pathname);
  const showRehearsals = rehearsalsEnabled(student);
  const showLogs = practiceLogsEnabled(student);
  const many = bookingLinks.length > 1;
  const navItems = showRehearsals ? PRIMARY_NAV : PRIMARY_NAV.filter((item) => item.id !== "rehearsals");

  const go = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const openRecord = () => {
    navigate("/student/rehearsals?record=1");
    onNavigate?.();
  };

  const logPractice = () => {
    onNavigate?.();
    openLog();
  };

  return (
    <div className="flex h-full flex-col px-4 pb-5 pt-[26px]">
      {/* Brand */}
      <div className="flex items-center gap-3 px-2 pb-[20px]">
        <div className="flex h-[42px] w-[42px] items-center justify-center overflow-hidden rounded-[12px] border border-[var(--studio-divider-strong)] bg-black">{/* brand-lint-ignore: true-black logo tile, matches StudioSidebar + the logo PNG's black backdrop */}
          <img src="/logo192.png" alt="Hans" className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="font-sans text-lg font-bold leading-none text-[var(--text-primary)]">Hans</div>
          <div className="mt-[3px] truncate font-sans text-[13px] text-[var(--text-tertiary)]">
            {displayName(student) || "Student"}
          </div>
        </div>
      </div>

      {/* Role badge */}
      <div className="mb-5 flex items-center justify-center gap-2 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-[14px] py-[9px]">
        <Icon name="graduation" size={15} strokeWidth={1.9} className="text-[var(--accent-yellow-text)]" />
        <span className="font-sans text-[13px] font-semibold text-[var(--accent-yellow-text)]">Student Portal</span>
      </div>

      {/* The single, signature yellow gesture: Log practice, or Record
          rehearsal where logging is off. Neither when both are off. */}
      {showLogs ? (
        <button
          type="button"
          onClick={logPractice}
          data-testid="student-sidebar-log"
          className="mb-3 flex items-center justify-center gap-[10px] rounded-[14px] bg-[var(--accent-yellow)] px-4 py-[14px] font-sans text-base font-bold text-[var(--text-inverse)] shadow-[0_6px_24px_var(--accent-yellow-glow)] transition-transform duration-150 hover:-translate-y-px active:scale-[0.98]"
        >
          <Icon name="plus" size={19} strokeWidth={2.4} className="text-[var(--text-inverse)]" />
          Log practice
        </button>
      ) : showRehearsals ? (
        <button
          type="button"
          onClick={openRecord}
          data-testid="student-sidebar-record"
          className="mb-3 flex items-center justify-center gap-[10px] rounded-[14px] bg-[var(--accent-yellow)] px-4 py-[14px] font-sans text-base font-bold text-[var(--text-inverse)] shadow-[0_6px_24px_var(--accent-yellow-glow)] transition-transform duration-150 hover:-translate-y-px active:scale-[0.98]"
        >
          <Icon name="mic" size={19} strokeWidth={2.2} className="text-[var(--text-inverse)]" />
          Record rehearsal
        </button>
      ) : null}

      {/* Book a lesson: each teacher's live booking page, in a new tab. */}
      {bookingLinks.map((link) => (
        <a
          key={link.user_id}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onNavigate?.()}
          title={bookHint(link)}
          data-testid={`student-sidebar-book-${link.user_id}`}
          className="mb-2 flex items-center gap-[10px] rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-[14px] py-[10px] font-sans text-[15px] font-semibold text-[var(--text-primary)] transition-colors duration-150 hover:bg-[var(--studio-interactive)]"
        >
          <Icon name="calendar" size={17} className="text-[var(--accent-yellow-text)]" />
          <span className="min-w-0 flex-1 truncate">{bookLabel(link, many)}</span>
          <Icon name="external" size={14} className="text-[var(--text-tertiary)]" />
        </a>
      ))}
      <div className="mb-[14px]" />

      {/* Primary nav */}
      <nav className="flex flex-col gap-1">
        {navItems.map((item) => (
          <NavItem key={item.id} item={item} active={active === item.id} onSelect={go} />
        ))}
      </nav>

      <div className="flex-1" />

      {/* Sign out + credit */}
      <div className="border-t border-[var(--studio-divider)] pt-3">
        <button
          type="button"
          onClick={() => logout?.()}
          data-testid="student-sign-out"
          className="flex w-full items-center gap-[14px] rounded-[12px] px-[14px] py-[11px] text-left transition-colors duration-150 hover:bg-[var(--studio-raised)]"
        >
          <Icon name="logout" size={20} strokeWidth={1.7} className="text-[var(--text-tertiary)]" />
          <span className="font-sans text-base font-medium text-[var(--text-tertiary)]">Sign out</span>
        </button>
        <div className="px-[14px] pt-[10px] font-mono text-[11px] tracking-[1px] text-[var(--text-quaternary)]">
          by {companyName()}
        </div>
      </div>
    </div>
  );
}

export default StudentSidebarInner;
